import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Search, Disc3, Loader2, HelpCircle } from 'lucide-react';

// Currency symbols for display
const CURRENCY_SYMBOLS = {
  DKK: 'kr', SEK: 'kr', NOK: 'kr',
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF',
  CAD: 'CA$', AUD: 'A$', NZD: 'NZ$',
  JPY: '¥', CNY: '¥', KRW: '₩',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  BRL: 'R$', MXN: 'MX$', INR: '₹',
};

// Format price from cents to full currency
const formatPrice = (cents, currency = 'DKK') => {
  if (!cents) return '-';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const amount = (cents / 100).toLocaleString('da-DK');
  // Put symbol after amount for Nordic currencies, before for others
  if (['DKK', 'SEK', 'NOK'].includes(currency)) {
    return `${amount} ${symbol}`;
  }
  return `${symbol}${amount}`;
};

export default function PublicSale() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showGradingGuide, setShowGradingGuide] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await publicAPI.getSaleList(token);
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This sale list was not found or has been deactivated.');
        } else {
          setError('An error occurred while loading the sale list.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Filter and sort items
  const filteredItems = (data?.items || [])
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.artist?.toLowerCase().includes(query) ||
        item.title?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price_low':
          return (a.sale_price || 0) - (b.sale_price || 0);
        case 'price_high':
          return (b.sale_price || 0) - (a.sale_price || 0);
        case 'artist':
          return (a.artist || '').localeCompare(b.artist || '', 'da');
        case 'newest':
        default:
          return 0;
      }
    });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
            <Disc3 className="w-8 h-8 text-zinc-600" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Sale list not available</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data?.items || data.items.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
                <Disc3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">{data?.title || 'For Sale'}</h1>
                <p className="text-xs text-zinc-500">Vinyllog</p>
              </div>
            </div>
          </div>
        </header>
        
        <main className="max-w-6xl mx-auto px-4 py-16 text-center">
          <p className="text-zinc-400">No records marked for sale yet.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
                <Disc3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">{data?.title || 'For Sale'}</h1>
                <p className="text-xs text-zinc-500">
                  {data?.total} {data?.total === 1 ? 'record' : 'records'} for sale
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowGradingGuide(true)}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 gap-1.5"
              data-testid="grading-guide-btn"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Grading</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Search and sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search artist or album..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
              data-testid="public-search-input"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white" data-testid="public-sort-select">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="price_low">Price (low-high)</SelectItem>
              <SelectItem value="price_high">Price (high-low)</SelectItem>
              <SelectItem value="artist">Artist</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Items grid - mobile-first 2 columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="public-sales-grid">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden"
              data-testid={`public-item-${item.id}`}
            >
              {/* Cover image */}
              <div className="aspect-square bg-zinc-800">
                {item.cover_image ? (
                  <img
                    src={item.cover_image}
                    alt={`${item.artist} - ${item.title}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Disc3 className="w-10 h-10 text-zinc-700" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="font-medium text-sm text-white line-clamp-1">{item.title}</h3>
                <p className="text-xs text-zinc-400 line-clamp-1">{item.artist}</p>
                
                {/* Grades - compact chips */}
                <div className="flex gap-1.5 mt-2">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-300">
                    Media: {item.condition_vinyl}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-300">
                    Sleeve: {item.condition_cover}
                  </Badge>
                </div>
                
                {/* Price - prominent */}
                <div className="mt-2 text-lg font-bold text-violet-400">
                  {formatPrice(item.sale_price, item.currency)}
                </div>
                
                {/* Optional note */}
                {item.sale_note && (
                  <p className="text-[11px] text-zinc-500 mt-1.5 line-clamp-2 italic">
                    &quot;{item.sale_note}&quot;
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact section */}
        <div className="mt-8 p-4 bg-zinc-900 border border-zinc-800 rounded-lg text-center">
          <p className="text-zinc-400 text-sm">
            Contact seller via the platform where you got this link (e.g. Facebook).
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-zinc-600">
          Powered by <a href="https://vinyllog.app" className="text-violet-500 hover:underline">Vinyllog</a>
        </p>
      </footer>

      {/* Grading Guide Dialog */}
      <Dialog open={showGradingGuide} onOpenChange={setShowGradingGuide}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-violet-500" />
              Vinyl and Cover Grading
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Goldmine standard. Use the same scale for both record and cover.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2 text-sm">
            <p className="text-zinc-400 italic">
              Be conservative – better to grade slightly lower than too high.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-violet-400">M (Mint)</h4>
                <p className="text-zinc-300">Brand new. Unopened or played without any visible or audible signs of use. Very rare in practice.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-violet-400">NM (Near Mint)</h4>
                <p className="text-zinc-300">Almost perfect. Few or no visible marks. Plays without noise. Cover is sharp with minimal handling signs.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-violet-400">VG+ (Very Good Plus)</h4>
                <p className="text-zinc-300">Lightly used. Small hairlines or surface marks may be visible but only minimally affect sound. Light background noise may occur. Cover may have slight edge wear or small pressure marks.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-violet-400">VG (Very Good)</h4>
                <p className="text-zinc-300">Clearly used but fully playable. Multiple marks and surface noise, especially in quiet passages, but no skipping. Cover may have ring wear, small bends, writing, or minor splits.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-violet-400">G (Good) / G+</h4>
                <p className="text-zinc-300">Heavily used. Significant noise, clicks or crackle may occur. Still playable without skipping. Cover may have major wear, splits, or stains.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-violet-400">F (Fair) / P (Poor)</h4>
                <p className="text-zinc-300">Very worn. Risk of skipping or repeating. Cover may be damaged or missing parts. Mostly for collectors who just need a placeholder.</p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-zinc-800">
              <h4 className="font-semibold text-violet-400 mb-2">Grading Tips</h4>
              <ul className="text-zinc-300 space-y-1 list-disc list-inside">
                <li>Check in good light and examine both sides.</li>
                <li>Play the record if in doubt.</li>
                <li>Grade record and cover separately.</li>
                <li>Original inner sleeves, inserts and stickers can add value.</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
